import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Constants from "../Utils/Constants";
import BlocViewModal from "./BlocViewModal";

export default function BlocMenu2() {
  const { t, i18n } = useTranslation("Blocs2");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const authHeaders = {
    Authorization: "Bearer " + token,
    "Accept-Language": i18n.language,
  };

  const jsonHeaders = {
    ...authHeaders,
    "Content-Type": "application/json",
  };

  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
  });

  const [blocs, setBlocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewBlocKey, setViewBlocKey] = useState(null);

  const openModal = (text, isError = false) => {
    Modal[isError ? "error" : "success"]({
      title: isError
        ? t("label_error") || "Erreur"
        : t("label_success") || "Succès",
      content: text,
    });
  };

  const handleView = (blocKey) => {
    setViewBlocKey(blocKey);
    setViewOpen(true);
  };

  const handleViewEdit = (blocKey) => {
    setViewOpen(false);
    navigate(`/blocs2/${blocKey}`);
  };

  const fetchBlocs = () => {
    setFetching(true);

    axios
      .get(`${Constants.SERVER_URL}/blocs`, {
        headers: authHeaders,
      })
      .then((res) => {
        setBlocs(res.data || []);
      })
      .catch((err) => {
        openModal(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Erreur lors du chargement des blocs",
          true
        );
      })
      .finally(() => {
        setFetching(false);
      });
  };

  useEffect(() => {
    setLoading(true);

    axios
      .get(`${Constants.SERVER_URL}/blocs`, {
        headers: authHeaders,
      })
      .then((res) => {
        setBlocs(res.data || []);
      })
      .catch((err) => {
        openModal(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Erreur lors du chargement des blocs",
          true
        );
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitCreate = () => {
    const data = {
      name: form.name?.trim(),
      description: form.description?.trim(),
    };

    if (!data.name) {
      message.warning(
        t("label_bloc_name_required") || "Le nom du bloc est requis"
      );
      return;
    }

    setCreating(true);

    axios
      .post(`${Constants.SERVER_URL}/create-bloc`, data, {
        headers: jsonHeaders,
      })
      .then((res) => {
        const blocKey = res?.data?.key;

        if (blocKey) {
          message.success(t(`Backend:${res?.data?.message}`) || "Bloc créé");
          navigate(`/blocs2/${blocKey}`, { replace: true });
        } else {
          openModal(t(`Backend:${res?.data?.message}`) || "Bloc créé");
          fetchBlocs();
        }

        setCreateOpen(false);
        setForm({
          name: "",
          description: "",
        });
      })
      .catch((err) => {
        openModal(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Création impossible",
          true
        );
      })
      .finally(() => {
        setCreating(false);
      });
  };

  const deleteBloc = (blocKey) => {
    axios
      .delete(
        `${Constants.SERVER_URL}/delete-bloc/${encodeURIComponent(blocKey)}`,
        {
          headers: authHeaders,
        }
      )
      .then((res) => {
        message.success(
          t(`Backend:${res?.data?.message}`) || "Bloc supprimé"
        );
        fetchBlocs();
      })
      .catch((err) => {
        openModal(
          t(`Backend:${err?.response?.data?.message}`) ||
            "Suppression impossible",
          true
        );
      });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return blocs;

    return blocs.filter((bloc) => {
      const name = String(bloc?.name || "").toLowerCase();
      const description = String(bloc?.description || "").toLowerCase();
      const key = String(bloc?.key || "").toLowerCase();

      return name.includes(q) || description.includes(q) || key.includes(q);
    });
  }, [blocs, search]);

  return (
    <div
      style={{
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Space
        align="center"
        style={{
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("label_blocs_title") || "Blocs d’exercices"}
        </Typography.Title>

        <Space wrap>
          <Segmented
            value={view}
            onChange={setView}
            options={[
              {
                value: "grid",
                icon: <AppstoreOutlined />,
                label: t("label_grid") || "Grille",
              },
              {
                value: "list",
                icon: <UnorderedListOutlined />,
                label: t("label_list") || "Liste",
              },
            ]}
          />

          <Tooltip title={t("label_refresh") || "Rafraîchir"}>
            <Button
              icon={<ReloadOutlined spin={fetching} />}
              onClick={fetchBlocs}
            />
          </Tooltip>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            {t("label_new_bloc") || "Nouveau bloc"}
          </Button>
        </Space>
      </Space>

      <Space>
        <Input.Search
          allowClear
          placeholder={t("label_search_bloc") || "Rechercher…"}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ maxWidth: 420 }}
        />

        {fetching && <Tag>MAJ…</Tag>}
      </Space>

      <div style={{ minHeight: 240 }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 48,
            }}
          >
            <Spin tip={t("label_loading_blocs") || "Chargement des blocs…"} />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description={t("label_no_blocs") || "Aucun bloc."} />
        ) : view === "grid" ? (
          <Row gutter={[16, 16]}>
            {filtered.map((bloc) => (
              <Col key={bloc.key} xs={24} sm={12} md={8} lg={6} xl={6}>
                <Card
                  hoverable
                  title={
                    <Typography.Text strong ellipsis style={{ maxWidth: 200 }}>
                      {bloc.name || "Sans nom"}
                    </Typography.Text>
                  }
                  actions={[
                    <Tooltip title={t("label_edit") || "Éditer"} key="edit">
                      <EditOutlined
                        onClick={() => navigate(`/blocs2/${bloc.key}`)}
                      />
                    </Tooltip>,

                    <Tooltip title={t("label_view") || "Voir"} key="view">
                      <EyeOutlined onClick={() => handleView(bloc.key)} />
                    </Tooltip>,

                    <Popconfirm
                      key="delete"
                      title={
                        t("label_confirm_delete") || "Supprimer ce bloc ?"
                      }
                      okText={t("label_delete") || "Supprimer"}
                      cancelText={t("label_cancel") || "Annuler"}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteBloc(bloc.key)}
                    >
                      <DeleteOutlined style={{ color: "#ff4d4f" }} />
                    </Popconfirm>,
                  ]}
                >
                  <Typography.Paragraph ellipsis={{ rows: 3 }}>
                    {bloc.description || "—"}
                  </Typography.Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {filtered.map((bloc) => (
              <Card key={bloc.key} size="small">
                <Space
                  align="start"
                  style={{
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>
                      {bloc.name || "Sans nom"}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {bloc.description || "—"}
                    </Typography.Text>
                  </Space>

                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => navigate(`/blocs2/${bloc.key}`)}
                    >
                      {t("label_edit") || "Éditer"}
                    </Button>

                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => handleView(bloc.key)}
                    >
                      {t("label_view") || "Voir"}
                    </Button>

                    <Popconfirm
                      title={
                        t("label_confirm_delete") || "Supprimer ce bloc ?"
                      }
                      okText={t("label_delete") || "Supprimer"}
                      cancelText={t("label_cancel") || "Annuler"}
                      okButtonProps={{ danger: true }}
                      onConfirm={() => deleteBloc(bloc.key)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        {t("label_delete") || "Supprimer"}
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              </Card>
            ))}
          </Space>
        )}
      </div>

      <Modal
        title={t("label_create_bloc_title") || "Créer un bloc"}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText={t("label_create") || "Créer"}
        okButtonProps={{ loading: creating }}
        onOk={onSubmitCreate}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            placeholder={t("label_bloc_name") || "Nom du bloc"}
            value={form.name}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                name: event.target.value,
              }))
            }
            maxLength={80}
          />

          <Input.TextArea
            placeholder={
              t("label_bloc_description") || "Description (optionnel)"
            }
            value={form.description}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={500}
          />
        </Space>
      </Modal>

      <BlocViewModal
        open={viewOpen}
        blocKey={viewBlocKey}
        onClose={() => setViewOpen(false)}
        onEdit={handleViewEdit}
      />
    </div>
  );
}